import React, { useMemo, useState } from "react";
import { withRouter } from "react-router-dom";
import {
  Form,
  Icon,
  Input,
  Button,
  Card,
  notification,
  Alert,
} from "antd";
import { useFreire } from "../../utils/freireContext";
import { signUp, ONBOARDING_ORIGIN } from "../../services/onboarding";
import { isWemob } from "../../utils/is-wemob";
import * as texts from "./locales";
import "./index.css";

const PASSWORD_RULES = {
  length: /.{8,}/,
  upper: /[A-Z]/,
  lower: /[a-z]/,
  number: /\d/,
};

const OnboardingForm = ({ form, history, location }) => {
  const { freire } = useFreire();
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const query = useMemo(() => new URLSearchParams(location.search), [
    location.search,
  ]);
  const origin = query.get("origin");
  const returnTo = query.get("returnTo");

  const onboardingOrigin =
    origin === "cpms" ? ONBOARDING_ORIGIN.CPMS : ONBOARDING_ORIGIN.ADMIN;

  const password = form.getFieldValue("password") || "";
  const ruleChecks = {
    length: PASSWORD_RULES.length.test(password),
    upper: PASSWORD_RULES.upper.test(password),
    lower: PASSWORD_RULES.lower.test(password),
    number: PASSWORD_RULES.number.test(password),
  };

  const validateAllPasswordRules = (_, value, cb) => {
    if (!value) return cb();
    if (
      !PASSWORD_RULES.length.test(value) ||
      !PASSWORD_RULES.upper.test(value) ||
      !PASSWORD_RULES.lower.test(value) ||
      !PASSWORD_RULES.number.test(value)
    ) {
      return cb(freire(texts.ERROR_WEAK_PASSWORD));
    }
    cb();
  };

  const validatePasswordConfirmation = (_, value, cb) => {
    if (value && value !== form.getFieldValue("password")) {
      return cb(freire(texts.PASSWORDS_DO_NOT_MATCH));
    }
    cb();
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    form.validateFields(async (err, values) => {
      if (err) return;

      setSubmitError(null);
      setLoading(true);
      try {
        await signUp({
          email: values.email.trim(),
          password: values.password,
          displayName: values.displayName.trim(),
          onboardingOrigin,
          keepSession: isWemob,
        });

        if (isWemob) {
          history.push("/ativacao");
        } else {
          const successQuery = returnTo ? `?returnTo=${returnTo}` : "";
          history.push(`/onboarding-complete${successQuery}`);
        }
      } catch (error) {
        const code = error?.code;
        if (code === "auth/email-already-in-use") {
          setSubmitError(freire(texts.ERROR_EMAIL_IN_USE));
        } else if (code === "auth/invalid-email") {
          setSubmitError(freire(texts.ERROR_INVALID_EMAIL));
        } else if (code === "auth/weak-password") {
          setSubmitError(freire(texts.ERROR_WEAK_PASSWORD));
        } else if (code === "auth/network-request-failed") {
          setSubmitError(freire(texts.ERROR_NETWORK));
        } else if (error?.message === "MONGO_UPSERT_FAILED") {
          setSubmitError(freire(texts.ERROR_MONGO_INCONSISTENT));
          notification.error({
            message: freire(texts.ERROR_MONGO_INCONSISTENT),
            description: error.email,
          });
        } else {
          setSubmitError(freire(texts.ERROR_GENERIC));
        }
      } finally {
        setLoading(false);
      }
    });
  };

  const { getFieldDecorator } = form;

  return (
    <div className="onboarding-container">
      <div className="onboarding-card-wrapper">
        <Card
          headStyle={{
            textAlign: "center",
            backgroundColor: "#001529",
            color: "white",
          }}
          title={freire(texts.ONBOARDING_TITLE)}
        >
          <p style={{ marginBottom: 16 }}>
            {freire(texts.ONBOARDING_SUBTITLE)}
          </p>

          {submitError && (
            <Alert
              type="error"
              message={submitError}
              style={{ marginBottom: 16 }}
              showIcon
            />
          )}

          <Form onSubmit={handleSubmit}>
            <Form.Item label={freire(texts.DISPLAY_NAME)}>
              {getFieldDecorator("displayName", {
                rules: [{ required: true, message: freire(texts.REQUIRED) }],
              })(
                <Input
                  prefix={<Icon type="user" />}
                  size="large"
                  autoComplete="name"
                />
              )}
            </Form.Item>

            <Form.Item label={freire(texts.EMAIL)}>
              {getFieldDecorator("email", {
                rules: [
                  { required: true, message: freire(texts.REQUIRED) },
                  { type: "email", message: freire(texts.INVALID_EMAIL) },
                ],
              })(
                <Input
                  prefix={<Icon type="mail" />}
                  size="large"
                  type="email"
                  autoComplete="email"
                />
              )}
            </Form.Item>

            <Form.Item label={freire(texts.PASSWORD)}>
              {getFieldDecorator("password", {
                rules: [
                  { required: true, message: freire(texts.REQUIRED) },
                  { validator: validateAllPasswordRules },
                ],
              })(
                <Input.Password
                  prefix={<Icon type="lock" />}
                  size="large"
                  autoComplete="new-password"
                />
              )}
              <ul className="onboarding-password-rules">
                {[
                  ["length", texts.PASSWORD_RULE_LENGTH],
                  ["upper", texts.PASSWORD_RULE_UPPER],
                  ["lower", texts.PASSWORD_RULE_LOWER],
                  ["number", texts.PASSWORD_RULE_NUMBER],
                ].map(([key, label]) => (
                  <li
                    key={key}
                    className={
                      ruleChecks[key] ? "rule-valid" : "rule-invalid"
                    }
                  >
                    <Icon type={ruleChecks[key] ? "check-circle" : "close-circle"} />
                    {freire(label)}
                  </li>
                ))}
              </ul>
            </Form.Item>

            <Form.Item label={freire(texts.PASSWORD_CONFIRMATION)}>
              {getFieldDecorator("passwordConfirmation", {
                rules: [
                  { required: true, message: freire(texts.REQUIRED) },
                  { validator: validatePasswordConfirmation },
                ],
              })(
                <Input.Password
                  prefix={<Icon type="lock" />}
                  size="large"
                  autoComplete="new-password"
                />
              )}
            </Form.Item>

            <Form.Item>
              <Button
                htmlType="submit"
                type="primary"
                loading={loading}
                style={{ width: "100%", backgroundColor: "#001529" }}
              >
                {freire(texts.SUBMIT)}
              </Button>
            </Form.Item>

            <div className="onboarding-footer">
              <Button type="link" onClick={() => history.push("/")}>
                {freire(texts.BACK_TO_LOGIN)}
              </Button>
            </div>
          </Form>
        </Card>
      </div>
    </div>
  );
};

const Onboarding = Form.create({ name: "onboarding" })(OnboardingForm);
export default withRouter(Onboarding);
